import type { AgentView, DeliveryView, MessageView } from "@walkie/protocol"
import { useMemo } from "react"
import { glyphMap, STATE_RANK } from "../lib/agents"
import { deliveryState } from "../lib/delivery"
import { isWithdrawn } from "../lib/messages"
import { useStore } from "./store"

export function useGlyphs() {
	const agents = useStore(s => s.agents)
	return useMemo(() => glyphMap(Object.values(agents)), [agents])
}

export function useMyAgentIds(): Set<string> {
	const agents = useStore(s => s.agents)
	const me = useStore(s => s.me?.user.id)
	return useMemo(
		() =>
			new Set(
				Object.values(agents)
					.filter(a => a.userId === me)
					.map(a => a.id),
			),
		[agents, me],
	)
}

export function useAgentsByHandle(): Map<string, AgentView> {
	const agents = useStore(s => s.agents)
	return useMemo(
		() => new Map(Object.values(agents).map(a => [a.handle.toLowerCase(), a])),
		[agents],
	)
}

export const isOpenRequest = (m: MessageView) =>
	!!m.request &&
	(m.request.status === "open" || m.request.status === "claimed") &&
	!isWithdrawn(m)

export function useOpenRequests(): MessageView[] {
	const messages = useStore(s => s.messages)
	return useMemo(
		() =>
			Object.values(messages)
				.filter(isOpenRequest)
				.sort((a, b) => b.seq - a.seq),
		[messages],
	)
}

export interface Failure {
	message: MessageView
	delivery: DeliveryView
}

export function useFailures(): Failure[] {
	const messages = useStore(s => s.messages)
	const now = useStore(s => s.now)
	return useMemo(() => {
		const out: Failure[] = []
		for (const m of Object.values(messages))
			for (const d of m.deliveries ?? [])
				if (deliveryState(d, now) === "failed") out.push({ message: m, delivery: d })
		return out.sort((a, b) => b.delivery.acceptedAt.localeCompare(a.delivery.acceptedAt))
	}, [messages, now])
}

/** Agents that authored a blocker that is still open: they are waiting on someone. */
export function useBlockedAgents(): Set<string> {
	const open = useOpenRequests()
	return useMemo(
		() =>
			new Set(
				open
					.filter(m => m.kind === "blocker" && m.author.type === "agent" && m.author.id)
					.map(m => m.author.id!),
			),
		[open],
	)
}

export function useFailingAgents(): Map<string, number> {
	const failures = useFailures()
	const agents = useStore(s => s.agents)
	return useMemo(() => {
		const n = new Map<string, number>()
		for (const f of failures)
			n.set(f.delivery.agentId, (n.get(f.delivery.agentId) ?? 0) + 1)
		for (const a of Object.values(agents))
			if ((a.stats?.failed ?? 0) > (n.get(a.id) ?? 0)) n.set(a.id, a.stats!.failed)
		return n
	}, [failures, agents])
}

export function useSortedAgents(): AgentView[] {
	const agents = useStore(s => s.agents)
	const blocked = useBlockedAgents()
	const failing = useFailingAgents()
	return useMemo(() => {
		const rank = (a: AgentView) =>
			a.state === "ended"
				? 9
				: blocked.has(a.id) || failing.has(a.id)
					? -1
					: STATE_RANK[a.state]
		return Object.values(agents).sort(
			(a, b) =>
				rank(a) - rank(b) ||
				Number(b.shared) - Number(a.shared) ||
				a.handle.localeCompare(b.handle),
		)
	}, [agents, blocked, failing])
}
